import { CommonModule, formatDate } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { TransportPlanningService } from '../transport-planning.service';
import { BulkRunDirectoryMetrics, BulkRunDirectoryQuery, BulkRunDirectoryRow } from './bulk-run-directory.model';

interface Filters {
  search: string;
  runNumber: string;
  statuses: string[];
  profile: string;
  triggerType: string;
  release: string;
  project: string;
  driver: string;
  vehicle: string;
  origin: string;
  destination: string;
  horizonFrom: string;
  horizonTo: string;
  createdFrom: string;
  createdTo: string;
}

@Component({
  selector: 'app-bulk-run-directory',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, MultiSelectModule,
    SelectModule, TableModule, TagModule, PageShellComponent, PageHeroComponent,
    PageSearchPanelComponent, PageMetricStripComponent, PageDataPanelComponent],
  templateUrl: './bulk-run-directory.component.html',
  styleUrls: ['./bulk-run-directory.component.css']
})
export class BulkRunDirectoryComponent {
  runs: BulkRunDirectoryRow[] = [];
  metrics: BulkRunDirectoryMetrics = this.emptyMetrics();
  totalRecords = 0;
  first = 0;
  loading = false;
  message = '';
  filters: Filters = this.emptyFilters();
  expandedRunIds = new Set<number>();
  readonly statusOptions = ['QUEUED', 'RUNNING', 'REVIEW_READY', 'PARTIAL', 'APPLIED', 'FAILED', 'CANCELLED']
    .map(value => ({ label: this.label(value), value }));
  readonly triggerOptions = [
    { label: 'Any trigger', value: '' },
    { label: 'Interactive', value: 'INTERACTIVE' },
    { label: 'Scheduled', value: 'SCHEDULED' }
  ];
  private lastEvent: TableLazyLoadEvent = { first: 0, rows: 25, sortField: 'createdAt', sortOrder: -1 };
  private requestToken = 0;

  constructor(private service: TransportPlanningService, private session: SessionStorageService,
    private router: Router) {}

  load(event: TableLazyLoadEvent = this.lastEvent): void {
    this.lastEvent = event;
    const token = ++this.requestToken;
    const first = event.first ?? 0;
    const size = event.rows ?? 25;
    const query: BulkRunDirectoryQuery = {
      owningCompanyId: this.session.getCompany()?.id ?? 0,
      ...this.filters,
      page: Math.floor(first / size), size,
      sortField: typeof event.sortField === 'string' ? event.sortField : 'createdAt',
      sortOrder: event.sortOrder ?? -1
    };
    this.first = first;
    this.loading = true;
    this.message = '';
    this.service.searchBulkRunDirectory(query).subscribe({
      next: page => {
        if (token !== this.requestToken) return;
        this.runs = page.content ?? [];
        this.metrics = page.metrics ?? this.emptyMetrics();
        this.totalRecords = page.totalElements ?? 0;
        this.loading = false;
      },
      error: error => {
        if (token !== this.requestToken) return;
        this.message = error?.error?.message || 'Could not load bulk planning runs. Please retry.';
        this.runs = [];
        this.totalRecords = 0;
        this.loading = false;
      }
    });
  }

  applyFilters(): void { this.load({ ...this.lastEvent, first: 0 }); }
  clearFilters(): void { this.filters = this.emptyFilters(); this.applyFilters(); }
  refresh(): void { this.load(this.lastEvent); }
  activeFilterCount(): number {
    return Object.values(this.filters).filter(value => Array.isArray(value) ? value.length > 0 : !!value).length;
  }
  summaryMetrics(): PageMetric[] {
    return [
      { label: 'Matching runs', value: this.metrics.total, tone: 'info' },
      { label: 'In progress', value: this.metrics.queued + this.metrics.running },
      { label: 'Ready for review', value: this.metrics.reviewReady, tone: 'info' },
      { label: 'Applied', value: this.metrics.applied, tone: 'success' },
      { label: 'With exceptions', value: this.metrics.withExceptions, tone: 'warn' },
      { label: 'Failed', value: this.metrics.failed, tone: 'danger' }
    ];
  }
  open(run: BulkRunDirectoryRow): void { this.router.navigate(['/transport-planning/bulk-planning', run.runId]); }
  toggleDetails(runId: number): void {
    if (this.expandedRunIds.has(runId)) this.expandedRunIds.delete(runId);
    else this.expandedRunIds.add(runId);
  }
  date(value?: string): string { return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : '—'; }
  label(value?: string): string {
    return value ? value.replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase()) : 'Unknown';
  }
  severity(value?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    return value === 'APPLIED' ? 'success' : value === 'REVIEW_READY' ? 'info'
      : value === 'PARTIAL' || value === 'RUNNING' ? 'warn'
      : value === 'FAILED' || value === 'CANCELLED' ? 'danger' : 'secondary';
  }
  preview(values: string[], limit = 2): string {
    if (!values?.length) return '—';
    return values.slice(0, limit).join(', ') + (values.length > limit ? ` +${values.length - limit} more` : '');
  }
  private emptyFilters(): Filters {
    return { search: '', runNumber: '', statuses: [], profile: '', triggerType: '', release: '',
      project: '', driver: '', vehicle: '', origin: '', destination: '', horizonFrom: '',
      horizonTo: '', createdFrom: '', createdTo: '' };
  }
  private emptyMetrics(): BulkRunDirectoryMetrics {
    return { total: 0, queued: 0, running: 0, reviewReady: 0, applied: 0,
      failed: 0, cancelled: 0, withExceptions: 0 };
  }
}
